import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { motoGearCategoryService, motoGearService } from "@/api/services"
import type { MotoGearCategoryResponseDto } from "@/api/types/motoGearCategory.types"
import type { MotoGearResponseDto, MotoGearListParams } from "@/api/types/motoGear.types"
import Sidebar from "@/components/Sidebar"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Loader2, ArrowLeft, Edit2, Trash2, Plus, Save } from "lucide-react"

export default function MotoGearCategoryDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [category, setCategory] = useState<MotoGearCategoryResponseDto | null>(null)
  const [gears, setGears] = useState<MotoGearResponseDto[]>([])
  const [loading, setLoading] = useState(true)
  const [gearsLoading, setGearsLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  // Category edit states
  const [isEditingCategory, setIsEditingCategory] = useState(false)
  const [categoryFormData, setCategoryFormData] = useState({ name: "" })
  const [savingCategory, setSavingCategory] = useState(false)
  const [deletingCategory, setDeletingCategory] = useState(false)
  const [deleteCategoryDialogOpen, setDeleteCategoryDialogOpen] = useState(false)

  // Gears states
  const [params, setParams] = useState<MotoGearListParams>({
    page: 1,
    limit: 50,
    categoryId: id,
  })
  const [total, setTotal] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  // Gear dialog states
  const [createGearDialogOpen, setCreateGearDialogOpen] = useState(false)
  const [editGearDialogOpen, setEditGearDialogOpen] = useState(false)
  const [deleteGearDialogOpen, setDeleteGearDialogOpen] = useState(false)
  const [selectedGear, setSelectedGear] = useState<MotoGearResponseDto | null>(null)

  // Gear form states
  const [gearFormData, setGearFormData] = useState({ name: "", categoryId: id || "" })
  const [savingGear, setSavingGear] = useState(false)
  const [deletingGear, setDeletingGear] = useState(false)

  useEffect(() => {
    if (id) {
      loadCategory()
      setParams((prev) => ({ ...prev, categoryId: id }))
    }
  }, [id])

  useEffect(() => {
    if (id) {
      loadGears()
    }
  }, [params, id])

  const loadCategory = async () => {
    if (!id) return
    setLoading(true)
    setError("")
    try {
      const categoryData = await motoGearCategoryService.getMotoGearCategoryById(id)
      setCategory(categoryData)
      setCategoryFormData({ name: categoryData.name })
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to load category"
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const loadGears = async () => {
    if (!id) return
    setGearsLoading(true)
    setError("")
    try {
      const response = await motoGearService.getMotoGears(params)
      setGears(response.data)
      setTotal(response.total)
      // Sync currentPage with response, but only if it matches what we requested
      const responsePage = response.page || params.page || 1
      setCurrentPage(responsePage)
      setTotalPages(response.totalPages || Math.ceil(response.total / (params.limit || 50)))
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to load gears"
      setError(errorMessage)
      // On error, reset to page 1 if we're on an invalid page
      if (params.page !== undefined && params.page > totalPages && totalPages > 0) {
        setCurrentPage(1)
        setParams((prevParams) => ({ ...prevParams, page: 1 }))
      }
    } finally {
      setGearsLoading(false)
    }
  }

  const handleEditCategory = () => {
    setIsEditingCategory(true)
  }

  const handleCancelEditCategory = () => {
    setIsEditingCategory(false)
    if (category) {
      setCategoryFormData({ name: category.name })
    }
  }

  const handleSaveCategory = async () => {
    if (!id || !categoryFormData.name.trim()) {
      setError("Category name is required")
      return
    }

    setSavingCategory(true)
    setError("")
    setSuccess("")

    try {
      const updatedCategory = await motoGearCategoryService.updateMotoGearCategory(id, {
        name: categoryFormData.name.trim(),
      })
      setCategory(updatedCategory)
      setSuccess("Category updated successfully")
      setIsEditingCategory(false)
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to update category"
      setError(errorMessage)
    } finally {
      setSavingCategory(false)
    }
  }

  const handleDeleteCategory = async () => {
    if (!id) return

    setDeletingCategory(true)
    setError("")
    setDeleteCategoryDialogOpen(false)

    try {
      await motoGearCategoryService.deleteMotoGearCategory(id)
      navigate("/moto-gear-categories")
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to delete category"
      setError(errorMessage)
      setDeletingCategory(false)
    }
  }

  const handleCreateGear = () => {
    setGearFormData({ name: "", categoryId: id || "" })
    setSelectedGear(null)
    setCreateGearDialogOpen(true)
  }

  const handleEditGear = (gear: MotoGearResponseDto) => {
    setGearFormData({ name: gear.name, categoryId: gear.categoryId })
    setSelectedGear(gear)
    setEditGearDialogOpen(true)
  }

  const handleDeleteGear = (gear: MotoGearResponseDto) => {
    setSelectedGear(gear)
    setDeleteGearDialogOpen(true)
  }

  const handleSubmitCreateGear = async () => {
    if (!gearFormData.name.trim() || !gearFormData.categoryId) {
      setError("Gear name is required")
      return
    }

    setSavingGear(true)
    setError("")
    setSuccess("")

    try {
      await motoGearService.createMotoGear({
        name: gearFormData.name.trim(),
        categoryId: gearFormData.categoryId,
      })
      setSuccess("Gear created successfully")
      setCreateGearDialogOpen(false)
      setGearFormData({ name: "", categoryId: id || "" })
      loadGears()
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to create gear"
      setError(errorMessage)
    } finally {
      setSavingGear(false)
    }
  }

  const handleSubmitEditGear = async () => {
    if (!selectedGear || !gearFormData.name.trim()) {
      setError("Gear name is required")
      return
    }

    setSavingGear(true)
    setError("")
    setSuccess("")

    try {
      await motoGearService.updateMotoGear(selectedGear.id, {
        name: gearFormData.name.trim(),
      })
      setSuccess("Gear updated successfully")
      setEditGearDialogOpen(false)
      setGearFormData({ name: "", categoryId: id || "" })
      setSelectedGear(null)
      loadGears()
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to update gear"
      setError(errorMessage)
    } finally {
      setSavingGear(false)
    }
  }

  const handleConfirmDeleteGear = async () => {
    if (!selectedGear) return

    setDeletingGear(true)
    setError("")
    setDeleteGearDialogOpen(false)

    try {
      await motoGearService.deleteMotoGear(selectedGear.id)
      setSuccess("Gear deleted successfully")
      setSelectedGear(null)
      loadGears()
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to delete gear"
      setError(errorMessage)
      setDeletingGear(false)
    }
  }

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return
    setCurrentPage(newPage)
    setParams((prevParams) => ({ ...prevParams, page: newPage }))
  }

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <main className="flex-1 ml-64 p-8 bg-background min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </main>
      </div>
    )
  }

  if (!category) {
    return (
      <div className="flex">
        <Sidebar />
        <main className="flex-1 ml-64 p-8 bg-background min-h-screen">
          <div className="max-w-2xl">
            <Card className="p-6">
              <div className="text-center">
                <p className="text-destructive mb-4">{error || "Category not found"}</p>
                <Button onClick={() => navigate("/moto-gear-categories")} variant="outline">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Categories
                </Button>
              </div>
            </Card>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="flex">
      <Sidebar />

      <main className="flex-1 ml-64 p-8 bg-background min-h-screen">
        <div className="max-w-7xl">
          <div className="flex items-center gap-4 mb-8">
            <Button variant="ghost" size="sm" onClick={() => navigate("/moto-gear-categories")}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Categories
            </Button>
            <h1 className="text-3xl font-bold text-foreground">Category Details</h1>
          </div>

          {/* Success message */}
          {success && (
            <div className="bg-success/10 border border-success/30 rounded-lg p-3 mb-6">
              <p className="text-sm text-success">{success}</p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 mb-6">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Category Information */}
          <Card className="p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground">Category Information</h2>
              {!isEditingCategory && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={handleEditCategory}>
                    <Edit2 className="mr-2 h-4 w-4" />
                    Edit
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setDeleteCategoryDialogOpen(true)}
                    disabled={deletingCategory}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </div>
              )}
            </div>

            {isEditingCategory ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="category-name">Category Name</Label>
                  <Input
                    id="category-name"
                    value={categoryFormData.name}
                    onChange={(e) => setCategoryFormData({ name: e.target.value })}
                    onKeyDown={(e) => e.key === "Enter" && handleSaveCategory()}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={handleSaveCategory} disabled={savingCategory}>
                    {savingCategory ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save
                      </>
                    )}
                  </Button>
                  <Button variant="outline" onClick={handleCancelEditCategory} disabled={savingCategory}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Name</Label>
                  <p className="text-foreground font-medium">{category.name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Created At</Label>
                  <p className="text-foreground font-medium">
                    {new Date(category.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Updated At</Label>
                  <p className="text-foreground font-medium">
                    {new Date(category.updatedAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            )}
          </Card>

          {/* Gears Section */}
          <Card>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-foreground">Gears</h2>
                <Button onClick={handleCreateGear}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Gear
                </Button>
              </div>

              {gearsLoading ? (
                <div className="p-8 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : gears.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <p>No gears found for this category</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Created At</TableHead>
                          <TableHead>Updated At</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {gears.map((gear) => (
                          <TableRow key={gear.id}>
                            <TableCell className="font-medium">{gear.name}</TableCell>
                            <TableCell>
                              {new Date(gear.createdAt).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              {new Date(gear.updatedAt).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditGear(gear)}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteGear(gear)}
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="p-4 border-t border-border flex items-center justify-between">
                      <div className="text-sm text-muted-foreground">
                        Showing {((currentPage - 1) * (params.limit || 50)) + 1} to{" "}
                        {Math.min(currentPage * (params.limit || 50), total)} of {total} gears
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </Button>
                        <span className="text-sm text-muted-foreground">
                          Page {currentPage} of {totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </Card>
        </div>
      </main>

      {/* Delete Category Dialog */}
      <Dialog open={deleteCategoryDialogOpen} onOpenChange={setDeleteCategoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Gear Category</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{category.name}"? This will also delete all gears
              in this category. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteCategoryDialogOpen(false)}
              disabled={deletingCategory}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteCategory} disabled={deletingCategory}>
              {deletingCategory ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Gear Dialog */}
      <Dialog open={createGearDialogOpen} onOpenChange={setCreateGearDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Moto Gear</DialogTitle>
            <DialogDescription>Add a new motorcycle gear to this category</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="create-gear-name">Gear Name</Label>
              <Input
                id="create-gear-name"
                placeholder="e.g., Full-face Helmet"
                value={gearFormData.name}
                onChange={(e) => setGearFormData({ ...gearFormData, name: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && handleSubmitCreateGear()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateGearDialogOpen(false)}
              disabled={savingGear}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmitCreateGear} disabled={savingGear}>
              {savingGear ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Gear Dialog */}
      <Dialog open={editGearDialogOpen} onOpenChange={setEditGearDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Moto Gear</DialogTitle>
            <DialogDescription>Update the motorcycle gear information</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-gear-name">Gear Name</Label>
              <Input
                id="edit-gear-name"
                placeholder="e.g., Full-face Helmet"
                value={gearFormData.name}
                onChange={(e) => setGearFormData({ ...gearFormData, name: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && handleSubmitEditGear()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditGearDialogOpen(false)}
              disabled={savingGear}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmitEditGear} disabled={savingGear}>
              {savingGear ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                "Update"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Gear Dialog */}
      <Dialog open={deleteGearDialogOpen} onOpenChange={setDeleteGearDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Moto Gear</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedGear?.name}"? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteGearDialogOpen(false)}
              disabled={deletingGear}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmDeleteGear} disabled={deletingGear}>
              {deletingGear ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
